/* ============================================================
   A modal sheet. Escape and the backdrop close it; focus starts inside. The
   page behind it blurs and dims; the sheet itself arrives with a touch of
   spring. Plain on purpose — the editors are forms, and a form in a card is
   all they need.
   ============================================================ */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export { inputClass } from './ui';

export function Dialog({
  title,
  onClose,
  children,
  footer,
  width = 'md',
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg';
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const first = ref.current?.querySelector<HTMLElement>('input,select,textarea,button');
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const max = { sm: 'md:max-w-sm', md: 'md:max-w-md', lg: 'md:max-w-lg' }[width];
  // Portalled to <body>: `position: fixed` is measured against the nearest
  // transformed/filtered ancestor, not the viewport, and a dialog opened from
  // inside an animated card (Settings → Choose a plan) was being boxed into
  // that card and overdrawn by its siblings. The body has no such ancestor.
  return createPortal(
    <div className="fade-in fixed inset-0 z-20 flex items-end justify-center bg-[rgba(27,27,31,0.32)] backdrop-blur-[6px] md:items-center md:p-6" onMouseDown={onClose}>
      {/* a bottom sheet on a phone, a centred card on a desktop */}
      <div
        ref={ref}
        role="dialog"
        aria-modal
        aria-label={title}
        className={`surface-pop dialog-in max-h-[92vh] w-full overflow-y-auto rounded-b-none rounded-t-[22px] p-5 pb-[calc(20px+env(safe-area-inset-bottom))] ${max} md:rounded-[22px] md:p-6`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mx-auto -mt-1 mb-3 h-1 w-10 rounded-full bg-hairline md:hidden" aria-hidden />
        {title ? <h2 className="font-display mb-4 text-[24px] font-semibold leading-none text-ink md:mb-5 md:text-[26px]">{title}</h2> : null}
        <div className="space-y-4">{children}</div>
        {footer ? <div className="mt-5 flex flex-wrap justify-end gap-2 md:mt-6">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function Label({ children }: { children: React.ReactNode }) {
  return <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[1px] text-meta">{children}</span>;
}
